import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useConnection } from '@solana/wallet-adapter-react';
import { fetchAllPopperNFTs, fetchNFTMetadata } from '../utils/popperverseProgram';
import NFTStatsCard, { readStatsFromReserved } from '../components/NFTStatsCard';
import './Home.css';

const Home = () => {
  const { connection } = useConnection();
  const [solPrice, setSolPrice] = useState(null);
  const [featuredProducts, setFeaturedProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showStatsModal, setShowStatsModal] = useState(false);
  const [statsProduct, setStatsProduct] = useState(null);
  const USD_PRICE = parseFloat(process.env.REACT_APP_7IN_PRICE) || 49.99; // 7-inch plushie price

  // Fetch SOL price
  useEffect(() => {
    const fetchSolPrice = async () => {
      try {
        const response = await fetch('https://api.coingecko.com/api/v3/simple/price?ids=solana&vs_currencies=usd');
        const data = await response.json();
        setSolPrice(data.solana.usd);
      } catch (error) {
        setSolPrice(200); // Fallback
      }
    };
    fetchSolPrice();
  }, []);

  // Load featured products from round 0 NFTs with product images
  useEffect(() => {
    const loadFeaturedProducts = async () => {
      if (!connection) return;
      
      setLoading(true);
      
      try {
        console.log('[Home] Fetching round 0 NFTs for featured products...');
        
        // Fetch all PopperNFTs from round 0 (with or without product images)
        const allNFTs = await fetchAllPopperNFTs(connection, { votingEligibleOnly: false });
        // Filter to round 0 NFTs, excluding archived ones
        const round0NFTs = allNFTs.filter(nft => nft.createdInRound === 0 && !nft.isArchived);
        
        console.log(`[Home] Found ${round0NFTs.length} round 0 NFTs`);
        
        if (round0NFTs.length === 0) {
          // Fallback to default products
          setFeaturedProducts([
            { 
              id: 1,
              name: 'SOL Dev Popper', 
              nftImage: '/Design/Products/1.png',
              productImage: '/Design/Products/1_product.png',
              frame: 'orange' 
            },
            { 
              id: 2,
              name: 'OG Popper', 
              nftImage: '/Design/Products/2.png',
              productImage: '/Design/Products/2_product.png',
              frame: 'blue' 
            }
          ]);
          setLoading(false);
          return;
        }
        
        // Fetch metadata for each NFT and create product cards
        const productsWithMetadata = await Promise.all(
          round0NFTs.slice(0, 3).map(async (nft, index) => {
            const metadata = await fetchNFTMetadata(connection, nft.nftMint);
            
            // Parse stats from reserved bytes
            const statsData = nft.reserved ? readStatsFromReserved(nft.reserved) : null;
            
            return {
              id: nft.nftMint,
              name: metadata?.name || `Featured Popper ${index + 1}`,
              description: metadata?.description || 'Community-created Popper design',
              nftImage: metadata?.image || '/Design/silhouette.png',
              productImage: nft.productImageUri,
              frame: index === 0 ? 'orange' : index === 1 ? 'blue' : 'purple',
              totalVotes: nft.totalVotes || 0,
              totalSales: nft.totalSales || 0,
              stats: statsData?.averages || { cuteness: 0, creativity: 0, popPower: 0, originality: 0, potential: 0 },
            };
          })
        );
        
        setFeaturedProducts(productsWithMetadata);
        console.log('[Home] Featured products loaded:', productsWithMetadata);
        
      } catch (error) {
        console.error('[Home] Failed to load featured products:', error);
        // Fallback to default
        setFeaturedProducts([
          { 
            id: 1,
            name: 'SOL Dev Popper', 
            nftImage: '/Design/Products/1.png',
            productImage: '/Design/Products/1_product.png',
            frame: 'orange' 
          },
          { 
            id: 2,
            name: 'OG Popper', 
            nftImage: '/Design/Products/2.png',
            productImage: '/Design/Products/2_product.png',
            frame: 'blue' 
          }
        ]);
      } finally {
        setLoading(false);
      }
    };
    
    loadFeaturedProducts();
  }, [connection]);

  const calculateSolPrice = () => {
    if (!solPrice) return '0.30';
    return (USD_PRICE / solPrice).toFixed(4);
  };

  return (
    <div className="home-page">
      <div className="hero">
        <h2>Welcome to Popperverse!</h2>
        <p>Discover the cutest Popper plushies on Solana</p>
        <Link to="/shop" className="cta-button">Explore Collection</Link>
      </div>

      <div className="featured-section">
        <h3>Featured Poppers</h3>
        {loading ? (
          <div className="loading-notice">
            <p>Loading featured products...</p>
          </div>
        ) : (
          <div className="product-grid centered-grid">
          {featuredProducts.map((product, index) => (
            <div 
              key={index} 
              className="product-card clickable"
              onClick={() => {
                setStatsProduct(product);
                setShowStatsModal(true);
              }}
            >
              <div className={`product-frame ${product.frame}-bg dual-image-container`}>
                <img 
                  src={product.productImage || product.nftImage} 
                  alt={`${product.name} - Plushie`}
                  className="product-image home-image primary-image"
                />
                {product.productImage && (
                  <img 
                    src={product.nftImage} 
                    alt={`${product.name} - Original Design`}
                    className="product-image home-image hover-image"
                  />
                )}
              </div>
              <h4>{product.name}</h4>
              <p className="price">{calculateSolPrice()} SOL</p>
              <p className="usd-price">${USD_PRICE} USD</p>
              <span className="view-stats-hint">👁️ Click to view stats</span>
            </div>
          ))}
          </div>
        )}
        <div className="view-all-container">
          <Link to="/shop" className="view-all-link">View All Products →</Link>
        </div>
      </div>

      <div className="cta-section">
        <h3>Join the Community!</h3>
        <p>Vote on new designs and help shape the future of Popperverse</p>
        <Link to="/vote" className="cta-button secondary">Vote Now</Link>
      </div>
      
      {/* NFT Stats Modal */}
      {showStatsModal && statsProduct && (
        <div className="modal-overlay" onClick={() => setShowStatsModal(false)}>
          <div className="stats-modal-container" onClick={(e) => e.stopPropagation()}>
            <NFTStatsCard
              name={statsProduct.name}
              description={statsProduct.description}
              imageUrl={statsProduct.nftImage}
              stats={statsProduct.stats}
              totalVotes={statsProduct.totalVotes}
              totalSales={statsProduct.totalSales}
              onClose={() => setShowStatsModal(false)}
              showCloseButton={true}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default Home;
